import * as Utils from '@js/modules/utils';
import History from '@js/modules/history';

import {fetchSearch} from '@js/actions/searchActions';

import {searchStore, SEARCH_ORDERS} from '@js/stores/searchStore';
import {mapStore} from '@js/stores/mapStore';

// The search page lists and maps every result at once (the API default is 96)
const SEARCH_RESULTS_LIMIT = 300;


class SearchManager {
    _searchHistoryName = 'search_data';

    _mapLoaded = false;

    _mapEventsListened = false;

    _mapLoadedCallback = undefined;

    _searchChangeCallback = undefined;

    _searchedLocation = null;

    // Saved map view ("lat,lng,zoom") to restore instead of refitting on the results
    _initialView = null;

    // Route picked in the suggestions, selected once its results are on the map
    _rideToSelect = null;

    // [lng, lat] of an "around me" search, marked on the map
    _userPosition = null;

    // Query and area whose results gave the current filter bounds
    _boundsKey = null;

    /* Public methods
    ******************** */
    initialize = (searchCallback) => {
        this._watchMapLoad();

        if (searchCallback) {
            this._searchChangeCallback = searchCallback;
        }

        const previousSearchData = this._withUrlLocation(History.getPreviousState(this._searchHistoryName, {useUrlParams: true}));

        if (Utils.isPresent(previousSearchData)) {
            this._setInitialFilters(previousSearchData);

            this._initialView = previousSearchData.view || null;

            this._performSearch(previousSearchData, {ignoreHistory: true});

            if (this._searchChangeCallback) {
                this._searchChangeCallback({
                    query: previousSearchData.query,
                    queryDisplay: previousSearchData.queryDisplay,
                    location: previousSearchData.location,
                    rideId: previousSearchData.related_type === 'ride' ? parseInt(previousSearchData.related_id, 10) || null : null,
                    nearMe: String(previousSearchData.near_me) === 'true'
                });
            }
        }

        this._onHistoryChange((newSearchData) => {
            this._initialView = newSearchData.view || null;

            this._performSearch(newSearchData, {ignoreHistory: true});

            if (this._searchChangeCallback) {
                this._searchChangeCallback({
                    query: newSearchData.query,
                    queryDisplay: newSearchData.queryDisplay,
                    location: newSearchData.location,
                    rideId: newSearchData.related_type === 'ride' ? parseInt(newSearchData.related_id, 10) || null : null,
                    nearMe: String(newSearchData.near_me) === 'true'
                });
            }
        });
    };

    fetchSearch = (params, searchOptions = {}) => {
        searchStore.isSearching = true;

        const searchParams = {
            query: params.query,
            queryDisplay: params.queryDisplay,
            order: params.order || searchStore.order,
            ...params.filters
        };

        // The centre is the user's position: marked on the map (kept in the URL for the way back)
        if (params.nearMe) {
            searchParams.near_me = true;
        }

        // A route picked in the suggestions: searched alone, then selected on the map
        if (params.rideId) {
            searchParams.related_type = 'ride';
            searchParams.related_id = params.rideId;
        }

        if (params.location && (params.location.bbox || params.location.center)) {
            searchParams.location = {
                bbox: !!params.location.bbox ? [...params.location.bbox] : null,
                center: !!params.location.center ? [...params.location.center] : null
            };
        }

        // A new search frames its own results
        this._initialView = null;

        if (!searchOptions.ignoreHistory) {
            History.saveCurrentState(
                {
                    [this._searchHistoryName]: searchParams
                },
                searchParams,
                {
                    keepPreviousParams: false,
                    replaceOnly: searchOptions.replaceOnly ?? Utils.isPresent(params.filters)
                }
            );
        }

        return this._performSearch(searchParams);
    };

    currentViewport = () => mapStore.map?.currentViewport?.() || null;

    fitResults = () => {
        mapStore.map?.fitResults?.();
    };

    unselectRide = () => {
        mapStore.map?.unselectRide?.();
    };

    clearSearch = () => {
        searchStore.isSearching = false;
        searchStore.ridesLoadedFromMap = true;

        searchStore.query = null;
        searchStore.queryDisplay = null;

        searchStore.ridesData = [];
        searchStore.totalCount = 0;
        searchStore.canSearchArea = false;

        searchStore.selectedFilters = {};
        searchStore.availableFilters = [];
        searchStore.filterBounds = null;
        this._boundsKey = null;

        History.saveCurrentState(
            {},
            {}
        );

        this._showResultsOnMap();
    };

    /* Private Methods
    ******************** */
    _watchMapLoad = () => {
        mapStore.subscribe((mapState) => {
            if (mapState.map && !this._mapLoaded) {
                mapStore.map.onMapLoaded(() => {
                    if (this._mapLoaded) {
                        return;
                    }

                    this._mapLoaded = true;

                    if (this._mapLoadedCallback) {
                        setTimeout(() => {
                            this._mapLoadedCallback();
                            this._mapLoadedCallback = undefined;
                        }, 300);
                    }

                    this._listenMapEvents();
                });
            }
        });
    };

    // The URL parser returns "location[bbox]" and "location[center]" flat: a shared link must keep its place
    _withUrlLocation = (searchData) => {
        if (!searchData || searchData.location || (!searchData['location[bbox]'] && !searchData['location[center]'])) {
            return searchData;
        }

        const {'location[bbox]': bbox, 'location[center]': center, ...otherData} = searchData;

        return {...otherData, location: {bbox: bbox || null, center: center || null}};
    };

    _setInitialFilters = (initialFilters) => {
        if (!initialFilters) {
            return;
        }

        searchStore.selectedFilters = {
            rideTypes: initialFilters.rideTypes ? new Set(initialFilters.rideTypes) : null,
            levels: initialFilters.levels ? new Set(initialFilters.levels) : null,
            distance: initialFilters.distance ? new Set(initialFilters.distance) : null,
            duration: initialFilters.duration ? new Set(initialFilters.duration) : null,
            mine: initialFilters.mine ? true : null
        };

        // An unknown order (edited or stale link) would show an untranslated sort chip
        if (SEARCH_ORDERS.includes(initialFilters.order)) {
            searchStore.order = initialFilters.order;
        }
    };

    serializeFilters = (selectedFilters = searchStore.selectedFilters) => {
        const filters = {};

        Object.entries(selectedFilters || {})
            .forEach(([filterName, filterValue]) => {
                if (filterValue instanceof Set) {
                    filters[filterName] = Array.from(filterValue);
                } else if (filterValue) {
                    filters[filterName] = filterValue;
                }
            });

        return filters;
    };

    _onHistoryChange = (onHistoryCallback) => {
        History.onStateChange((newState) => {
            if (newState[this._searchHistoryName]) {
                onHistoryCallback(newState[this._searchHistoryName]);
            }
        });
    };

    _performSearch = (searchParams) => {
        this._rideToSelect = searchParams.related_type === 'ride' ? parseInt(searchParams.related_id, 10) || null : null;
        searchStore.pickedRideId = this._rideToSelect || undefined;
        this._userPosition = String(searchParams.near_me) === 'true' && searchParams.location?.center ? searchParams.location.center.map(parseFloat) : null;
        searchStore.ridesLoadedFromMap = false;
        searchStore.canSearchArea = false;
        this._searchedLocation = searchParams.location || null;

        // eslint-disable-next-line camelcase, no-unused-vars
        const {near_me, ...apiParams} = searchParams;

        // light: the list and the map read no track, heart data, tags nor pictures (300 rides at once)
        return fetchSearch({...apiParams, per_page: SEARCH_RESULTS_LIMIT, light: true})
            .then((response) => {
                searchStore.isSearching = false;
                searchStore.query = searchParams.query;
                searchStore.queryDisplay = searchParams.queryDisplay;

                if (response) {
                    searchStore.ridesData = response.rides || [];
                    searchStore.totalCount = response.totalCount?.rides || 0;

                    searchStore.visibleRideIds = response.rides?.map((ride) => ride.id) || [];

                    searchStore.availableFilters = response.aggregations?.rides || [];

                    this._updateFilterBounds(searchParams, response.aggregations?.rides);

                    this._onMapLoaded(this._showResultsOnMap);
                } else {
                    searchStore.ridesData = [];
                    searchStore.totalCount = 0;

                    searchStore.visibleRideIds = [];

                    searchStore.availableFilters = [];
                }

                return searchParams;
            });
    };

    // Bounds come from the results of a new query or area, never from the global min/max of every ride
    _updateFilterBounds = (searchParams, aggregations) => {
        const boundsKey = JSON.stringify([searchParams.query, searchParams.location, searchParams.mine]);
        if (boundsKey === this._boundsKey && searchStore.filterBounds) {
            return;
        }

        this._boundsKey = boundsKey;

        // An active range narrows min/max_<range> (Searchkick keeps a filter on aggregations named
        // otherwise): the bounds come from the same search without the ranges
        if (searchParams.distance || searchParams.duration) {
            // eslint-disable-next-line no-unused-vars
            const {distance, duration, ...unranged} = searchParams;

            fetchSearch({...unranged, per_page: 1, light: true, bounds_only: true}, {silentErrors: true, silentOffline: true})
                .then((boundsResponse) => {
                    if (this._boundsKey === boundsKey) {
                        this._applyFilterBounds(searchParams, boundsResponse?.aggregations?.rides || aggregations);
                    }
                })
                .catch(() => this._applyFilterBounds(searchParams, aggregations));


            return;
        }

        this._applyFilterBounds(searchParams, aggregations);
    };

    _applyFilterBounds = (searchParams, aggregations) => {
        const range = (name, step) => {
            const min = parseFloat(aggregations?.[`min_${name}`]);
            const max = parseFloat(aggregations?.[`max_${name}`]);
            if (!Number.isFinite(min) || !Number.isFinite(max)) {
                return null;
            }

            // A range restored from the URL narrowed the aggregations: widen back to include it
            const selected = searchParams[name]?.map(parseFloat);

            return {
                min: Math.floor(Math.min(min, selected?.[0] ?? min) / step) * step,
                max: Math.ceil(Math.max(max, selected?.[1] ?? max) / step) * step
            };
        };

        searchStore.filterBounds = {
            distance: range('distance', 1000),
            duration: range('duration', 300)
        };
    };

    _handleViewportChange = (viewport) => {
        if (!viewport?.bbox || searchStore.ridesLoadedFromMap) {
            return;
        }

        const [west, south, east, north] = viewport.bbox;
        searchStore.visibleRideIds = searchStore.ridesData
            .filter((ride) => {
                const [lat, lng] = ride.coordinates || [];

                return lat >= south && lat <= north && lng >= west && lng <= east;
            })
            .map((ride) => ride.id);

        if (viewport.byUser) {
            searchStore.canSearchArea = true;

            this._saveView(viewport);
        }
    };

    _saveView = Utils.debounce((viewport) => {
        const view = [viewport.center.lat.toFixed(5), viewport.center.lng.toFixed(5), viewport.zoom.toFixed(2)].join(',');
        const searchData = History.getPreviousState(this._searchHistoryName) || {};

        History.saveCurrentState(
            {[this._searchHistoryName]: {...searchData, view}},
            {view},
            {keepPreviousParams: true, replaceOnly: true}
        );
    }, 300);

    _listenMapEvents = () => {
        if (!mapStore.map || this._mapEventsListened) {
            return;
        }

        this._mapEventsListened = true;

        if (typeof mapStore.map.onVisibleMarkersChanged === 'function') {
            mapStore.map.onRidesChanged((ridesData) => {
                searchStore.isSearching = false;
                searchStore.ridesData = ridesData;
            });

            // Map mode only: query results are filtered on the viewport below
            mapStore.map.onVisibleMarkersChanged((markers) => {
                if (!searchStore.ridesLoadedFromMap) {
                    return;
                }

                searchStore.visibleRideIds = markers
                    .filter((marker) => marker.type === 'ride')
                    .map((marker) => marker.id);
            });

            mapStore.map.onRideMarkerClick((marker) => {
                searchStore.highlightedRideId = marker?.id;
            });

            mapStore.map.onRideMarkerHover((marker) => {
                searchStore.highlightedRideId = marker?.id;
            });
        }

        if (typeof mapStore.map.onViewportChanged === 'function') {
            mapStore.map.onViewportChanged(this._handleViewportChange);
        }

        if (typeof mapStore.map.onRideSelected === 'function') {
            mapStore.map.onRideSelected((rideId) => {
                searchStore.selectedRideId = rideId;
            });
        }
    };

    _showResultsOnMap = () => {
        if (!mapStore.map) {
            return;
        }

        if (searchStore.ridesLoadedFromMap) {
            mapStore.map.toggleSearchOnZoom(true);
            mapStore.map.fetchSelected();

            return;
        } else {
            mapStore.map.toggleSearchOnZoom(false);
        }

        mapStore.map.unselectRide?.();

        if (this._userPosition) {
            mapStore.map.showUserPosition?.(this._userPosition);
        } else {
            mapStore.map.clearUserPosition?.();
        }

        mapStore.map.showSelected({
            ridesData: searchStore.ridesData,
            location: this._searchedLocation,
            view: this._initialView,
            clearBounds: true
        });

        this._initialView = null;

        if (this._rideToSelect && searchStore.ridesData.some((ride) => ride.id === this._rideToSelect)) {
            mapStore.map.selectRide?.(this._rideToSelect);
        }

        // The fit may not move the map (no moveend): filter on the view right away
        this._handleViewportChange({...mapStore.map.currentViewport?.(), byUser: false});
    };

    _onMapLoaded = (callback) => {
        if (!this._mapLoaded) {
            this._mapLoadedCallback = callback;
        } else {
            callback();
        }
    };
}

export default new SearchManager();
